import { useState, useRef, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { usePermissions } from '@/hooks/usePermissions'
import {
  Title,
  Text,
  Card,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Button,
  TextInput,
  Flex,
} from '@tremor/react'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  PhoneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  MinusCircleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getOperators, createOperator, deleteOperator, generateInstallToken, syncFromVoip, updateOperator, type SyncFromVoipResponse, type SyncResultItem } from '@/api/operators'
import type { Operator } from '@/types'

// Editable cell component with click-to-edit and auto-save
interface EditableCellProps {
  value: string
  operatorId: number
  field: 'fullname' | 'project' | 'location' | 'phone_number'
  onSave: (id: number, field: string, value: string) => void
  isSaving?: boolean
  placeholder?: string
}

function EditableCell({ value, operatorId, field, onSave, isSaving, placeholder = '-' }: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value || '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  useEffect(() => {
    setEditValue(value || '')
  }, [value])

  const handleSave = () => {
    const trimmedValue = editValue.trim()
    if (trimmedValue !== (value || '')) {
      onSave(operatorId, field, trimmedValue)
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setEditValue(value || '')
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="w-full px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        disabled={isSaving}
      />
    )
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="group cursor-pointer flex items-center gap-1 px-1 py-0.5 -mx-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      title="Cliquer pour modifier"
    >
      <span className={value ? '' : 'text-gray-400'}>{value || placeholder}</span>
      <PencilIcon className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  )
}

function getStatusBadge(status: string) {
  const config: Record<string, { color: 'green' | 'gray' | 'yellow' | 'red'; label: string }> = {
    active: { color: 'green', label: 'Actif' },
    inactive: { color: 'gray', label: 'Inactif' },
    pending_setup: { color: 'yellow', label: 'En attente' },
    disconnected: { color: 'red', label: 'Déconnecté' },
  }
  const { color, label } = config[status] || { color: 'gray', label: status }
  return <Badge color={color}>{label}</Badge>
}

export function Operators() {
  // Onboarding is a write-only flow — viewers get redirected to the
  // read-only /realtime page. Backend already gates POST/DELETE/
  // install-token at supervisor+, this guard just stops the page
  // from rendering buttons that wouldn't work.
  const { canOnboard, role } = usePermissions()
  if (role && !canOnboard) {
    return <Navigate to="/realtime" replace />
  }
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [syncResult, setSyncResult] = useState<SyncFromVoipResponse | null>(null)
  const [installUrl, setInstallUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  // Pre-token modal: ask for Tailscale config before generating the install URL
  const [installConfigOpId, setInstallConfigOpId] = useState<number | null>(null)
  const [tsUseVpn, setTsUseVpn] = useState(false)
  const [tsAuthkey, setTsAuthkey] = useState('')
  const [tsHostname, setTsHostname] = useState('')

  // Form state
  const [formData, setFormData] = useState({
    fullname: '',
    extension: '',
    project: '',
    location: '',
    sip_password: '',
  })

  // Sync form state
  const [syncFormData, setSyncFormData] = useState({
    project: '',
    location: '',
  })

  const { data: operators = [], isLoading } = useQuery({
    queryKey: ['operators'],
    queryFn: getOperators,
  })

  const createMutation = useMutation({
    mutationFn: createOperator,
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['operators'] })
      setShowCreateModal(false)
      setFormData({ fullname: '', extension: '', project: '', location: '', sip_password: '' })
      // Open install-config modal so the user picks Tailscale on/off before token generation
      setInstallConfigOpId(data.id)
    },
    onError: () => { alert('Erreur lors de la création de l\'opérateur') },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteOperator,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operators'] })
    },
    onError: () => { alert('Erreur lors de la suppression de l\'opérateur') },
  })

  const tokenMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload?: { use_vpn_sip?: boolean; tailscale_authkey?: string; tailscale_hostname?: string } }) =>
      generateInstallToken(id, payload || {}),
    onSuccess: (data) => {
      setInstallUrl(data.installation_url)
      setInstallConfigOpId(null)
      setTsUseVpn(false)
      setTsAuthkey('')
      setTsHostname('')
    },
    onError: (error: any) => {
      const detail = error.response?.data?.detail
      const msg = Array.isArray(detail)
        ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join(' ; ')
        : (typeof detail === 'string' ? detail : 'Erreur lors de la génération du token')
      alert(msg)
    },
  })

  const syncMutation = useMutation({
    mutationFn: syncFromVoip,
    onSuccess: (data) => {
      setSyncResult(data)
      if (data.created > 0) {
        queryClient.invalidateQueries({ queryKey: ['operators'] })
      }
    },
    onError: () => { alert('Erreur lors de la synchronisation VoIP') },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { fullname?: string; project?: string; location?: string } }) =>
      updateOperator(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operators'] })
    },
    onError: () => { alert('Erreur lors de la modification') },
  })

  const handleInlineEdit = (id: number, field: string, value: string) => {
    updateMutation.mutate({ id, data: { [field]: value } })
  }

  const filteredOperators = operators
    .filter((op: Operator) =>
      search === '' ||
      op.fullname.toLowerCase().includes(search.toLowerCase()) ||
      op.extension.includes(search) ||
      op.project.toLowerCase().includes(search.toLowerCase())
    )
    // Alphabetical by fullname, French locale, accent-insensitive.
    .sort((a: Operator, b: Operator) =>
      (a.fullname || '').localeCompare(b.fullname || '', 'fr', { sensitivity: 'base' })
    )

  const handleCreate = () => {
    const { sip_password, ...data } = formData
    createMutation.mutate(sip_password ? { ...data, sip_password } : data)
  }

  const handleDelete = (id: number) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cet opérateur ?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleGenerateToken = (id: number) => {
    setTsUseVpn(false)
    setTsAuthkey('')
    setTsHostname('')
    setInstallConfigOpId(id)
  }

  const handleConfirmInstallConfig = () => {
    if (installConfigOpId == null) return
    if (tsUseVpn && (!tsAuthkey.trim() || !tsHostname.trim())) {
      alert('Authkey et hostname sont requis pour Tailscale')
      return
    }
    tokenMutation.mutate({
      id: installConfigOpId,
      payload: tsUseVpn
        ? { use_vpn_sip: true, tailscale_authkey: tsAuthkey.trim(), tailscale_hostname: tsHostname.trim() }
        : { use_vpn_sip: false },
    })
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handlePreviewSync = () => {
    syncMutation.mutate({ ...syncFormData, dry_run: true })
  }

  const handleConfirmSync = () => {
    syncMutation.mutate({ ...syncFormData, dry_run: false })
  }

  const closeSyncModal = () => {
    setShowSyncModal(false)
    setSyncResult(null)
    setSyncFormData({ project: '', location: '' })
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <Title>Opérateurs</Title>
          <Text>Gestion des opérateurs du centre d'appels</Text>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" icon={PhoneIcon} onClick={() => setShowSyncModal(true)}>
            Sync VoIP
          </Button>
          <Button icon={PlusIcon} onClick={() => setShowCreateModal(true)}>
            Nouvel opérateur
          </Button>
        </div>
      </div>

      {/* Search */}
      <TextInput
        icon={MagnifyingGlassIcon}
        placeholder="Rechercher par nom, extension ou projet..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full sm:max-w-md mb-4 sm:mb-6"
      />

      {/* Stats */}
      <div className="flex flex-wrap gap-2 sm:gap-4 mb-4 sm:mb-6">
        <Badge color="blue" size="lg">
          {operators.length} opérateur{operators.length > 1 ? 's' : ''}
        </Badge>
        <Badge color="green" size="lg">
          {operators.filter((o: Operator) => o.status === 'active').length} actif{operators.filter((o: Operator) => o.status === 'active').length > 1 ? 's' : ''}
        </Badge>
        <Badge color="yellow" size="lg">
          {operators.filter((o: Operator) => o.status === 'pending_setup').length} en attente
        </Badge>
      </div>

      {/* Table (desktop) / Cards (mobile) */}
      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </Card>
      ) : filteredOperators.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Text className="text-gray-500 dark:text-gray-400 mb-4">
              {operators.length === 0
                ? "Aucun opérateur. Créez votre premier opérateur pour commencer."
                : "Aucun opérateur ne correspond à votre recherche."}
            </Text>
            {operators.length === 0 && (
              <Button icon={PlusIcon} onClick={() => setShowCreateModal(true)}>
                Créer un opérateur
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden md:block">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Nom</TableHeaderCell>
                  <TableHeaderCell>Extension</TableHeaderCell>
                  <TableHeaderCell>Projet</TableHeaderCell>
                  <TableHeaderCell>Localisation</TableHeaderCell>
                  <TableHeaderCell>Téléphone</TableHeaderCell>
                  <TableHeaderCell>Statut</TableHeaderCell>
                  <TableHeaderCell>Agent</TableHeaderCell>
                  <TableHeaderCell>Dernière connexion</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOperators.map((op: Operator) => (
                  <TableRow key={op.id}>
                    <TableCell className="font-medium">
                      <EditableCell
                        value={op.fullname}
                        operatorId={op.id}
                        field="fullname"
                        onSave={handleInlineEdit}
                        isSaving={updateMutation.isPending}
                      />
                    </TableCell>
                    <TableCell>{op.extension}</TableCell>
                    <TableCell>
                      <EditableCell
                        value={op.project}
                        operatorId={op.id}
                        field="project"
                        onSave={handleInlineEdit}
                        isSaving={updateMutation.isPending}
                      />
                    </TableCell>
                    <TableCell>
                      <EditableCell
                        value={op.location || ''}
                        operatorId={op.id}
                        field="location"
                        onSave={handleInlineEdit}
                        isSaving={updateMutation.isPending}
                        placeholder="-"
                      />
                    </TableCell>
                    <TableCell>
                      <EditableCell
                        value={op.phone_number || ''}
                        operatorId={op.id}
                        field="phone_number"
                        onSave={handleInlineEdit}
                        isSaving={updateMutation.isPending}
                        placeholder="+212..."
                      />
                    </TableCell>
                    <TableCell>{getStatusBadge(op.status)}</TableCell>
                    <TableCell>
                      {op.agent_version
                        ? <Badge color="blue" size="xs">v{op.agent_version}</Badge>
                        : <Badge color="gray" size="xs">Non installé</Badge>
                      }
                    </TableCell>
                    <TableCell>
                      {op.last_seen
                        ? new Date(op.last_seen).toLocaleString('fr-FR')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Flex justifyContent="start" className="gap-2">
                        <Button
                          size="xs"
                          variant="secondary"
                          icon={ArrowPathIcon}
                          onClick={() => handleGenerateToken(op.id)}
                          loading={tokenMutation.isPending}
                        >
                          Installer
                        </Button>
                        <Button
                          size="xs"
                          variant="secondary"
                          color="red"
                          icon={TrashIcon}
                          onClick={() => handleDelete(op.id)}
                          loading={deleteMutation.isPending}
                        >
                          Supprimer
                        </Button>
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filteredOperators.map((op: Operator) => (
              <Card key={op.id} className="!p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <Text className="font-semibold text-gray-900 dark:text-white truncate">{op.fullname || '—'}</Text>
                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                      ext. {op.extension} · {op.project || '—'}
                    </Text>
                  </div>
                  {getStatusBadge(op.status)}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {op.location && <div><span className="text-gray-400">📍</span> {op.location}</div>}
                  {op.phone_number && <div><span className="text-gray-400">📱</span> {op.phone_number}</div>}
                  <div className="col-span-2">
                    Agent: {op.agent_version
                      ? <Badge color="blue" size="xs">v{op.agent_version}</Badge>
                      : <Badge color="gray" size="xs">Non installé</Badge>}
                  </div>
                  {op.last_seen && (
                    <div className="col-span-2 text-gray-400">
                      Dernière: {new Date(op.last_seen).toLocaleString('fr-FR')}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    size="xs"
                    variant="secondary"
                    icon={ArrowPathIcon}
                    onClick={() => handleGenerateToken(op.id)}
                    loading={tokenMutation.isPending}
                    className="!min-h-[40px]"
                  >
                    Installer
                  </Button>
                  <Button
                    size="xs"
                    variant="secondary"
                    color="red"
                    icon={TrashIcon}
                    onClick={() => handleDelete(op.id)}
                    loading={deleteMutation.isPending}
                    className="!min-h-[40px]"
                  >
                    Supprimer
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-md sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-4">Nouvel opérateur</Title>
            <div className="space-y-4">
              <div>
                <Text className="mb-1">Nom complet *</Text>
                <TextInput
                  placeholder="Jean Dupont"
                  value={formData.fullname}
                  onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                />
              </div>
              <div>
                <Text className="mb-1">Extension (6 chiffres) *</Text>
                <TextInput
                  placeholder="100001"
                  maxLength={6}
                  value={formData.extension}
                  onChange={(e) => setFormData({ ...formData, extension: e.target.value })}
                />
              </div>
              <div>
                <Text className="mb-1">Projet *</Text>
                <TextInput
                  placeholder="Support Client"
                  value={formData.project}
                  onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                />
              </div>
              <div>
                <Text className="mb-1">Localisation</Text>
                <TextInput
                  placeholder="Agadir"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>
              <div>
                <Text className="mb-1">Mot de passe SIP</Text>
                <TextInput
                  type="password"
                  placeholder="Mot de passe du compte SIP"
                  value={formData.sip_password}
                  onChange={(e) => setFormData({ ...formData, sip_password: e.target.value })}
                />
                <Text className="text-xs text-gray-500 mt-1">Pour la configuration automatique de Linphone</Text>
              </div>
              <Flex justifyContent="end" className="gap-2 mt-6">
                <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={handleCreate}
                  loading={createMutation.isPending}
                  disabled={!formData.fullname || !formData.extension || !formData.project}
                >
                  Créer
                </Button>
              </Flex>
            </div>
          </Card>
        </div>
      )}

      {/* Install Config Modal — pick Tailscale on/off before generating the token */}
      {installConfigOpId !== null && !installUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-md sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-2">Options d'installation</Title>
            <Text className="text-sm text-gray-500 mb-4">
              Choisissez la route SIP pour cet opérateur. Linphone est toujours installé.
            </Text>
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={tsUseVpn}
                  onChange={(e) => setTsUseVpn(e.target.checked)}
                />
                <div>
                  <Text className="font-medium">Connexion via Tailscale VPN</Text>
                  <Text className="text-xs text-gray-500">
                    Active pour router le SIP via <code>emcall.emc</code> (overlay Tailscale).
                    Sinon route Internet par défaut.
                  </Text>
                </div>
              </label>

              {tsUseVpn && (
                <div className="space-y-3 pl-6">
                  <div>
                    <Text className="text-sm font-medium mb-1">Tailscale authkey</Text>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2 text-sm font-mono dark:bg-gray-800"
                      placeholder="hskey-auth-... ou tskey-..."
                      value={tsAuthkey}
                      onChange={(e) => setTsAuthkey(e.target.value)}
                    />
                    <Text className="text-xs text-gray-500 mt-1">Headscale: expire après 1h</Text>
                  </div>
                  <div>
                    <Text className="text-sm font-medium mb-1">Tailscale hostname</Text>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2 text-sm font-mono dark:bg-gray-800"
                      placeholder="operator-301001"
                      value={tsHostname}
                      onChange={(e) => setTsHostname(e.target.value)}
                    />
                    <Text className="text-xs text-gray-500 mt-1">Minuscules, chiffres, tirets — RFC 1123</Text>
                  </div>
                </div>
              )}
            </div>

            <Flex justifyContent="end" className="gap-2 mt-6">
              <Button variant="secondary" onClick={() => setInstallConfigOpId(null)}>
                Annuler
              </Button>
              <Button
                onClick={handleConfirmInstallConfig}
                loading={tokenMutation.isPending}
                disabled={tsUseVpn && (!tsAuthkey.trim() || !tsHostname.trim())}
              >
                Générer le lien
              </Button>
            </Flex>
          </Card>
        </div>
      )}

      {/* Install URL Modal */}
      {installUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-4">🔧 Commande d'installation</Title>
            <Text className="mb-4">
              Exécutez cette commande sur le PC Ubuntu de l'opérateur :
            </Text>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm break-all mb-4">
              {installUrl}
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
              <Text className="text-yellow-800 dark:text-yellow-200 text-sm">
                ⏱️ <strong>Important :</strong> Ce code est valide pendant <strong>30 minutes</strong> seulement et ne peut être utilisé qu'une seule fois.
              </Text>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <Text className="text-blue-800 dark:text-blue-200 text-sm font-medium mb-2">Instructions :</Text>
              <ol className="text-blue-700 dark:text-blue-300 text-sm space-y-1 list-decimal list-inside">
                <li>Copiez la commande ci-dessus</li>
                <li>Ouvrez un terminal sur le poste Ubuntu de l'opérateur</li>
                <li>Collez et exécutez la commande avec sudo</li>
                <li>L'installation se fera automatiquement</li>
              </ol>
            </div>
            <Flex justifyContent="end" className="gap-2">
              <Button
                variant="secondary"
                icon={ClipboardDocumentIcon}
                onClick={() => copyToClipboard(installUrl)}
                color={copied ? 'green' : 'gray'}
              >
                {copied ? 'Copié !' : 'Copier'}
              </Button>
              <Button onClick={() => { setInstallUrl(null); setCopied(false); }}>Fermer</Button>
            </Flex>
          </Card>
        </div>
      )}

      {/* Sync VoIP Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-4">
              <Flex justifyContent="start" className="gap-2">
                <PhoneIcon className="w-6 h-6" />
                Synchroniser depuis VoIP
              </Flex>
            </Title>

            {!syncResult ? (
              // Form to configure sync
              <div className="space-y-4">
                <Text className="text-gray-600 dark:text-gray-400">
                  Cette action va récupérer toutes les extensions depuis le serveur Asterisk et créer les opérateurs manquants.
                  Les nouveaux opérateurs seront créés avec l'extension comme nom (modifiable ensuite).
                </Text>

                <div>
                  <Text className="mb-1">Projet par défaut *</Text>
                  <TextInput
                    placeholder="Support Client"
                    value={syncFormData.project}
                    onChange={(e) => setSyncFormData({ ...syncFormData, project: e.target.value })}
                  />
                </div>

                <div>
                  <Text className="mb-1">Localisation par défaut</Text>
                  <TextInput
                    placeholder="Agadir"
                    value={syncFormData.location}
                    onChange={(e) => setSyncFormData({ ...syncFormData, location: e.target.value })}
                  />
                </div>

                <Flex justifyContent="end" className="gap-2 mt-6">
                  <Button variant="secondary" onClick={closeSyncModal}>
                    Annuler
                  </Button>
                  <Button
                    onClick={handlePreviewSync}
                    loading={syncMutation.isPending}
                    disabled={!syncFormData.project}
                  >
                    Prévisualiser
                  </Button>
                </Flex>
              </div>
            ) : (
              // Show sync results
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                    <Text className="text-blue-600 dark:text-blue-400 text-2xl font-bold">{syncResult.total_extensions}</Text>
                    <Text className="text-blue-600 dark:text-blue-400 text-xs">Extensions VoIP</Text>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                    <Text className="text-green-600 dark:text-green-400 text-2xl font-bold">{syncResult.created}</Text>
                    <Text className="text-green-600 dark:text-green-400 text-xs">À créer</Text>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                    <Text className="text-gray-600 dark:text-gray-400 text-2xl font-bold">{syncResult.already_exists}</Text>
                    <Text className="text-gray-600 dark:text-gray-400 text-xs">Existants</Text>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-center">
                    <Text className="text-yellow-600 dark:text-yellow-400 text-2xl font-bold">{syncResult.skipped}</Text>
                    <Text className="text-yellow-600 dark:text-yellow-400 text-xs">Ignorés</Text>
                  </div>
                </div>

                {/* Details list */}
                <div className="border rounded-lg dark:border-gray-700 max-h-64 overflow-y-auto overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Extension</th>
                        <th className="text-left p-2">Action</th>
                        <th className="text-left p-2">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncResult.details.map((item: SyncResultItem) => (
                        <tr key={item.extension} className="border-t dark:border-gray-700">
                          <td className="p-2 font-mono">{item.extension}</td>
                          <td className="p-2">
                            {item.action === 'created' && (
                              <Flex justifyContent="start" className="gap-1">
                                <CheckCircleIcon className="w-4 h-4 text-green-500" />
                                <span className="text-green-600 dark:text-green-400">Créer</span>
                              </Flex>
                            )}
                            {item.action === 'exists' && (
                              <Flex justifyContent="start" className="gap-1">
                                <MinusCircleIcon className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-500">Existe</span>
                              </Flex>
                            )}
                            {item.action === 'skipped' && (
                              <Flex justifyContent="start" className="gap-1">
                                <ExclamationCircleIcon className="w-4 h-4 text-yellow-500" />
                                <span className="text-yellow-600 dark:text-yellow-400">Ignoré</span>
                              </Flex>
                            )}
                          </td>
                          <td className="p-2 text-gray-500 text-xs">
                            {item.fullname && item.action === 'exists' && `Opérateur: ${item.fullname}`}
                            {item.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Flex justifyContent="end" className="gap-2 mt-6">
                  <Button variant="secondary" onClick={closeSyncModal}>
                    Fermer
                  </Button>
                  {syncResult.created > 0 && (
                    <Button
                      color="green"
                      onClick={handleConfirmSync}
                      loading={syncMutation.isPending}
                    >
                      Confirmer la création ({syncResult.created})
                    </Button>
                  )}
                </Flex>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
